import {ErrorHandler, Injectable, Injector, Optional} from '@angular/core';
import {WsFrontendError} from '@lifeislife/lifeislife-ws-api';
import {
  AppConfigService,
  AuthProvider,
  ErrorUtils,
  FrontendAppConfigKey,
  FrontendAppSwitch,
  RequestService,
  WindowRef,
} from '@lifeislife/lifeislife-domain';
import {Subject} from 'rxjs';
import {bufferWhen, debounceTime} from 'rxjs/operators';
import {Location} from '@angular/common';
import {AppHomeScreenInstallerService} from '@lifeislife/lifeislife-components';

@Injectable({
  providedIn: 'root',
})
export class CustomerFrontErrorHandler implements ErrorHandler {

  private errorToSendQueue$ = new Subject<WsFrontendError>();

  constructor(private injector: Injector,
              @Optional() private appInstallService: AppHomeScreenInstallerService,
              private location: Location,
              private windowRef: WindowRef,
              private requestService: RequestService) {

    // Buffer errors to send to backend in case of bursts
    const bufferCloseTimes$ = this.errorToSendQueue$.pipe(
      debounceTime(1000),
    );
    this.errorToSendQueue$.pipe(
      bufferWhen(() => bufferCloseTimes$),
    ).subscribe(errors => this.sendErrors(errors));
  }

  handleError(error: any) {
    console.warn(error);
    const curPath = this.location.path();

    const stackTraceTxt = ErrorUtils.createStackTrace(error);
    const message = error.message || '';
    const navigatorName = this.parseBrowser();

    const configService = this.getConfigService();
    const appVersion = configService.getApplicationVersion();
    const standalone = this.appInstallService == null ? null : this.appInstallService.isLaunchedFromHomeScreen();
    let appName = configService.getCurrentConfigValue(FrontendAppConfigKey.app_name) || 'UNKOWN';
    if (standalone) {
      appName += ` - Standalone`;
    }

    if (ErrorUtils.isErrorToDiscard(error)) {
      return;
    }
    const sendReport = configService.isSwitchCurrentlyEnabled(FrontendAppSwitch.front_error_report);
    if (!sendReport) {
      return;
    }

    const frontendError: WsFrontendError = {
      errorStack: stackTraceTxt,
      message: message,
      url: curPath,
      applicationName: appName,
      version: appVersion,
      navigator: navigatorName,
    };

    this.errorToSendQueue$.next(frontendError);
  }


  private parseBrowser() {
    const window = this.windowRef.getWindow();
    if (window.navigator) {
      return `user agent: ${window.navigator.userAgent}, vendor: ${window.navigator.vendor}`;
    } else {
      return 'inconnu';
    }
  }


  private sendErrors(errors: WsFrontendError[]) {
    if (errors.length === 0) {
      return;
    }

    const configService = this.getConfigService();
    const authProvider = this.getAuthProvider();
    ErrorUtils.sendErrors(errors, authProvider, configService, this.requestService);
  }

  // Resolve theses at runtime because this is dependency hell.
  private getAuthProvider(): AuthProvider | null {
    return this.injector.get(AuthProvider, null);
  }

  private getConfigService(): AppConfigService | null {
    return this.injector.get(AppConfigService, null);
  }

}
